import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import { Plus, Trash2, CheckCircle, Circle, Clock, Star } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const API = process.env.REACT_APP_API_URL || "https://app.fintogo.ai";

function emptyQuestion() {
    return {
        id: `LQ_${Date.now()}`,
        title: "",
        type: "single_choice",
        prompt: "",
        options: [
            { answer_text: "", is_correct: true },
            { answer_text: "", is_correct: false },
            { answer_text: "", is_correct: false },
            { answer_text: "", is_correct: false },
        ],
        time_limit: 20,
        points: 10,
        tags: ["livequiz"],
    };
}

export default function LiveQuizEditor({ moduleId, lessonId, lang = "de" }) {
    const { darkMode } = useTheme();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    // Laden: alle exercises holen, nur livequiz-tagged rausfiltern
    useEffect(() => {
        if (!moduleId || !lessonId) return;
        setLoading(true);
        fetch(`${API}/api/admin/module/${moduleId}/lesson/${lessonId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
            .then((r) => r.json())
            .then((data) => {
                const all = data?.quiz?.exercises || [];
                const lqQuestions = all.filter((e) => Array.isArray(e.tags) && e.tags.includes("livequiz"));
                setQuestions(lqQuestions);
                setLoading(false);
            })
            .catch(() => {
                setError("Fehler beim Laden der LiveQuiz-Fragen.");
                setLoading(false);
            });
    }, [moduleId, lessonId, lang]);

    // Speichern: komplette exercises (normale + livequiz) zusammenführen und PUT
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            // Zuerst alle exercises laden
            const res = await fetch(`${API}/api/admin/module/${moduleId}/lesson/${lessonId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            const data = await res.json();
            const allExercises = data?.quiz?.exercises || [];

            // Normale exercises behalten, livequiz ersetzen
            const normalExercises = allExercises.filter(
                (e) => !(Array.isArray(e.tags) && e.tags.includes("livequiz"))
            );
            const merged = [...normalExercises, ...questions];

            await fetch(`${API}/api/admin/module/${moduleId}/lesson/${lessonId}/exercises`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ exercises: merged }),
            });

            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch {
            setError("Fehler beim Speichern.");
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => setQuestions([...questions, emptyQuestion()]);

    const updateQuestion = (index, field, value) => {
        const updated = [...questions];
        updated[index] = { ...updated[index], [field]: value };
        setQuestions(updated);
    };

    const updateOption = (qIndex, oIndex, field, value) => {
        const updated = [...questions];
        const options = [...updated[qIndex].options];

        // Nur eine Antwort kann correct sein
        if (field === "is_correct" && value === true) {
            options.forEach((o, i) => (options[i] = { ...o, is_correct: i === oIndex }));
        } else {
            options[oIndex] = { ...options[oIndex], [field]: value };
        }

        updated[qIndex] = { ...updated[qIndex], options };
        setQuestions(updated);
    };

    const deleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    if (loading) return <div className="text-center py-5 text-muted">Lade LiveQuiz-Fragen...</div>;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="fw-bold mb-0">🎮 LiveQuiz-Fragen</h5>
                    <small className="text-muted">
                        {questions.length} Frage{questions.length !== 1 ? "n" : ""} · werden in der quiz.json gespeichert
                    </small>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={addQuestion}>
                        <Plus size={16} className="me-1" /> Frage hinzufügen
                    </Button>
                    <Button
                        variant={saved ? "success" : "primary"}
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Speichert..." : saved ? "✓ Gespeichert!" : "Speichern"}
                    </Button>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {questions.length === 0 && (
                <div className="text-center py-5 border rounded text-muted"
                    style={{ borderColor: "var(--border-color)" }}>
                    <p className="mb-2">Noch keine LiveQuiz-Fragen vorhanden.</p>
                    <Button variant="outline-primary" size="sm" onClick={addQuestion}>
                        <Plus size={16} className="me-1" /> Erste Frage erstellen
                    </Button>
                </div>
            )}

            {questions.map((q, qIndex) => (
                <Card key={q.id} className="mb-3 border-2" style={{ borderColor: "#4f46e5" }}>
                    <Card.Header className="d-flex justify-content-between align-items-center py-2"
                        style={{ background: darkMode ? "#2d2d5a" : "#f5f3ff" }}>
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg="primary" style={{ background: "#4f46e5" }}>#{qIndex + 1}</Badge>
                            <span className="fw-semibold">{q.title || "Neue Frage"}</span>
                        </div>
                        <Button variant="outline-danger" size="sm" onClick={() => deleteQuestion(qIndex)}>
                            <Trash2 size={14} />
                        </Button>
                    </Card.Header>

                    <Card.Body className="p-3">
                        <div className="row g-2 mb-3">
                            {/* ID */}
                            <Form.Group className="col-12 col-md-4">
                                <Form.Label className="small fw-semibold">ID</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={q.id}
                                    onChange={(e) => updateQuestion(qIndex, "id", e.target.value)}
                                    placeholder="z.B. M1_L1_LQ1"
                                />
                            </Form.Group>

                            {/* Titel */}
                            <Form.Group className="col-12 col-md-8">
                                <Form.Label className="small fw-semibold">Titel</Form.Label>
                                <Form.Control
                                    size="sm"
                                    value={q.title}
                                    onChange={(e) => updateQuestion(qIndex, "title", e.target.value)}
                                    placeholder="z.B. LiveQuiz 1 – Einnahmen berechnen"
                                />
                            </Form.Group>

                            {/* Zeitlimit */}
                            <Form.Group className="col-6 col-md-3">
                                <Form.Label className="small fw-semibold">
                                    <Clock size={13} className="me-1" />Zeit (Sek.)
                                </Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={5}
                                    max={120}
                                    value={q.time_limit}
                                    onChange={(e) => updateQuestion(qIndex, "time_limit", parseInt(e.target.value) || 20)}
                                />
                            </Form.Group>

                            {/* Punkte */}
                            <Form.Group className="col-6 col-md-3">
                                <Form.Label className="small fw-semibold">
                                    <Star size={13} className="me-1" />Punkte
                                </Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    min={1}
                                    value={q.points}
                                    onChange={(e) => updateQuestion(qIndex, "points", parseInt(e.target.value) || 10)}
                                />
                            </Form.Group>

                            {/* Frage */}
                            <Form.Group className="col-12">
                                <Form.Label className="small fw-semibold">Frage / Aufgabenstellung</Form.Label>
                                <Form.Control
                                    size="sm"
                                    as="textarea"
                                    rows={2}
                                    value={q.prompt}
                                    onChange={(e) => updateQuestion(qIndex, "prompt", e.target.value)}
                                    placeholder="z.B. Wie hoch sind die Gesamteinnahmen?"
                                />
                            </Form.Group>
                        </div>

                        {/* Antwortoptionen */}
                        <Form.Label className="small fw-semibold">Antwortmöglichkeiten</Form.Label>
                        <div className="d-flex flex-column gap-2">
                            {q.options.map((opt, oIndex) => (
                                <div key={oIndex} className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm p-0 border-0"
                                        onClick={() => updateOption(qIndex, oIndex, "is_correct", true)}
                                        title={opt.is_correct ? "Richtige Antwort" : "Als richtig markieren"}
                                    >
                                        {opt.is_correct
                                            ? <CheckCircle size={20} color="#22c55e" fill="#dcfce7" />
                                            : <Circle size={20} color="#9ca3af" />}
                                    </button>
                                    <Form.Control
                                        size="sm"
                                        value={opt.answer_text}
                                        onChange={(e) => updateOption(qIndex, oIndex, "answer_text", e.target.value)}
                                        placeholder={`Antwort ${oIndex + 1}`}
                                        style={{
                                            borderColor: opt.is_correct ? "#22c55e" : undefined,
                                            background: opt.is_correct ? (darkMode ? "#1a3a2a" : "#f0fdf4") : undefined,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <small className="text-muted mt-1 d-block">
                            🟢 Kreis anklicken = richtige Antwort markieren
                        </small>
                    </Card.Body>
                </Card>
            ))}

            {questions.length > 0 && (
                <div className="text-end mt-2">
                    <Button
                        variant={saved ? "success" : "primary"}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Speichert..." : saved ? "✓ Gespeichert!" : "💾 Alle Fragen speichern"}
                    </Button>
                </div>
            )}
        </div>
    );
}
